import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import { useBrandSettings } from '../hooks/useBrandSettings';

function LogoPlate({ children }) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 1.35,
        py: 1,
        borderRadius: 3,
        minWidth: 0,
        maxWidth: '44%',
        background:
          'linear-gradient(160deg, rgba(120, 200, 255, 0.16) 0%, rgba(255,255,255,0.06) 48%, rgba(255,255,255,0.03) 100%)',
        border: '1px solid rgba(140, 210, 255, 0.22)',
        boxShadow:
          '0 12px 32px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.04), inset 0 1px 0 rgba(255,255,255,0.18)',
        backdropFilter: 'blur(18px) saturate(1.35)',
        WebkitBackdropFilter: 'blur(18px) saturate(1.35)',
      }}
    >
      {children}
    </Box>
  );
}

function KeolisLogo({ size }) {
  const { t } = useTranslation();
  return (
    <Box
      component="img"
      src="/assets/logo-single.png"
      alt={t('brand.keolisLogo', { defaultValue: 'Keolis' })}
      sx={{
        width: size,
        height: size,
        objectFit: 'contain',
        display: 'block',
        filter: 'drop-shadow(0 2px 10px rgba(79,195,247,0.35))',
      }}
    />
  );
}

function SubsidiaryLogo({ src, height, onError }) {
  const { t } = useTranslation();
  return (
    <Box
      component="img"
      src={src}
      alt={t('brand.subsidiaryLogo', { defaultValue: 'Logo de la filiale' })}
      onError={onError}
      sx={{
        height,
        width: 'auto',
        maxWidth: '100%',
        objectFit: 'contain',
        display: 'block',
        filter: 'drop-shadow(0 2px 10px rgba(0,0,0,0.4))',
      }}
    />
  );
}

export default function BrandLogos() {
  const theme = useTheme();
  // Desktop / tablet: centered capsule. Phone: edges with margin.
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const settings = useBrandSettings();
  const [subsidiaryBroken, setSubsidiaryBroken] = useState(false);
  const subsidiaryLogo =
    !subsidiaryBroken && settings?.subsidiary_logo_base64
      ? settings.subsidiary_logo_base64
      : null;
  const onSubsidiaryError = () => setSubsidiaryBroken(true);

  return (
    <Box
      component="header"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1200,
        pt: isDesktop
          ? 2
          : 'max(14px, env(safe-area-inset-top, 0px))',
        px: isDesktop ? 3 : 0,
        pl: isDesktop
          ? undefined
          : 'max(20px, env(safe-area-inset-left, 0px))',
        pr: isDesktop
          ? undefined
          : 'max(20px, env(safe-area-inset-right, 0px))',
        pointerEvents: 'none',
        background: isDesktop
          ? 'transparent'
          : 'linear-gradient(180deg, rgba(8,18,36,0.72) 0%, rgba(8,18,36,0.28) 62%, rgba(8,18,36,0) 100%)',
        '@keyframes brandIn': {
          from: { opacity: 0, transform: 'translateY(-10px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        animation: 'brandIn 420ms cubic-bezier(0.22, 1, 0.36, 1)',
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
        },
      }}
    >
      {isDesktop ? (
        <Box
          sx={{
            pointerEvents: 'auto',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              px: 2.5,
              py: 1.35,
              borderRadius: 999,
              background: 'rgba(10, 22, 40, 0.72)',
              boxShadow: '0 16px 48px rgba(0,0,0,0.35)',
              backdropFilter: 'blur(18px)',
              WebkitBackdropFilter: 'blur(18px)',
            }}
          >
            <KeolisLogo size={56} />
            {subsidiaryLogo ? (
              <SubsidiaryLogo
                src={subsidiaryLogo}
                height={50}
                onError={onSubsidiaryError}
              />
            ) : null}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            pointerEvents: 'auto',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <LogoPlate>
            <KeolisLogo size={46} />
          </LogoPlate>
          {subsidiaryLogo ? (
            <LogoPlate>
              <SubsidiaryLogo
                src={subsidiaryLogo}
                height={42}
                onError={onSubsidiaryError}
              />
            </LogoPlate>
          ) : null}
        </Box>
      )}
    </Box>
  );
}
